import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { toast } from "sonner";
import { Footer } from "../../Components";
import { Button } from "../../Components/ui/buttons/button";
import { Eye } from "../../Components/ui/icons";
import Typography from "../../Components/Typography";
import { HomeContainer } from "../styles";
import { StyledMain } from "../Metadata/styles";
import { usePropitiousAreaJobsPolling } from "../../hooks/usePropitiousAreaJobsPolling";
import {
  PropitiousAreaJobResponse,
  PropitiousAreaJobStatus,
  PropitiousAreaJobStatusFilter,
} from "../../global/types/propitiousAreaJob";
import {
  getPropitiousAreaJob,
  listPropitiousAreaJobs,
} from "../../services/propitiousAreaJob";
import { getApiErrorMessage } from "../../utils/apiErrorMessage";

const STATUS_FILTERS: Array<{
  value: PropitiousAreaJobStatusFilter;
  label: string;
}> = [
  { value: "ALL", label: "Todos" },
  { value: "QUEUED", label: "Na fila" },
  { value: "RUNNING", label: "Em execução" },
  { value: "DONE", label: "Concluídos" },
  { value: "FAILED", label: "Falhos" },
];

const STATUS_LABELS: Record<PropitiousAreaJobStatus, string> = {
  QUEUED: "Na fila",
  RUNNING: "Em execução",
  DONE: "Concluído",
  FAILED: "Falhou",
};

const STATUS_COLORS: Record<
  PropitiousAreaJobStatus,
  "default" | "info" | "success" | "error"
> = {
  QUEUED: "default",
  RUNNING: "info",
  DONE: "success",
  FAILED: "error",
};

const ENERGY_LABELS: Record<string, string> = {
  solar: "Solar",
  eolica: "Eólica",
};

const DEFAULT_ROWS_PER_PAGE = 25;

const formatIsoDate = (value?: string | null) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const fmtArea = (km2?: number | null) =>
  km2 == null ? "N/A" : `${km2.toFixed(2)} km²`;

const fmtPower = (gw?: number | null) =>
  gw == null ? "N/A" : `${gw.toFixed(3)} GW`;

const fmtEnergy = (gwh?: number | null) =>
  gwh == null ? "N/A" : `${gwh.toFixed(1)} GWh`;

function StatusChip({ status }: { status: PropitiousAreaJobStatus }) {
  return (
    <Chip
      color={STATUS_COLORS[status]}
      label={STATUS_LABELS[status]}
      size="small"
      variant="outlined"
    />
  );
}

export function AreasPropicias() {
  const [jobs, setJobs] = useState<PropitiousAreaJobResponse[]>([]);
  const [statusFilter, setStatusFilter] =
    useState<PropitiousAreaJobStatusFilter>("ALL");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detailJob, setDetailJob] = useState<PropitiousAreaJobResponse | null>(
    null
  );

  // Jobs pendentes que não aparecem na página/filtro atual, mas ainda precisam
  // manter o polling vivo até terminarem.
  const [hiddenPendingCount, setHiddenPendingCount] = useState(0);

  const latestFetchIdRef = useRef(0);
  const previousStatusesRef = useRef<Map<number, PropitiousAreaJobStatus>>(
    new Map()
  );

  const fetchJobs = useCallback(async () => {
    const fetchId = latestFetchIdRef.current + 1;
    latestFetchIdRef.current = fetchId;

    setLoading(true);
    setError(null);

    try {
      const status = statusFilter === "ALL" ? undefined : statusFilter;
      const response = await listPropitiousAreaJobs({
        status,
        offset: page * rowsPerPage,
        limit: rowsPerPage,
      });

      // Ignora respostas fora de ordem: só a última chamada disparada aplica.
      if (fetchId !== latestFetchIdRef.current) return;

      const previousStatuses = previousStatusesRef.current;
      const nextStatuses = new Map(previousStatuses);

      const notifyTransition = (job: PropitiousAreaJobResponse) => {
        const previous = previousStatuses.get(job.id);
        if (
          previous &&
          (previous === "QUEUED" || previous === "RUNNING") &&
          (job.status === "DONE" || job.status === "FAILED")
        ) {
          toast(
            job.status === "DONE"
              ? "Área propícia estadual pronta"
              : "Falha no cálculo de área propícia estadual",
            {
              description: `${ENERGY_LABELS[job.energy_type] ?? job.energy_type} — job #${job.id}`,
            }
          );
        }
      };

      const visibleIds = new Set(response.items.map((job) => job.id));
      // Jobs pendentes que sumiram da página/filtro atual (ex.: filtro RUNNING,
      // o job vira DONE e deixa a lista): sem consultar por id, a transição —
      // e o toast — passariam despercebidos.
      const missingPendingIds = Array.from(previousStatuses.entries())
        .filter(
          ([id, status]) =>
            !visibleIds.has(id) && (status === "QUEUED" || status === "RUNNING")
        )
        .map(([id]) => id);

      for (const job of response.items) {
        notifyTransition(job);
        nextStatuses.set(job.id, job.status);
      }

      // Descarta o histórico que não é mais nem visível nem pendente, para o
      // mapa não crescer indefinidamente entre trocas de página/filtro.
      for (const id of Array.from(nextStatuses.keys())) {
        if (!visibleIds.has(id) && !missingPendingIds.includes(id)) {
          nextStatuses.delete(id);
        }
      }
      previousStatusesRef.current = nextStatuses;

      setJobs(response.items);
      setTotal(response.total);

      if (missingPendingIds.length > 0) {
        const results = await Promise.allSettled(
          missingPendingIds.map((id) => getPropitiousAreaJob(id))
        );
        if (fetchId !== latestFetchIdRef.current) return;
        for (const result of results) {
          if (result.status !== "fulfilled") continue;
          notifyTransition(result.value);
          nextStatuses.set(result.value.id, result.value.status);
        }
        setHiddenPendingCount(
          missingPendingIds.filter((id) => {
            const status = nextStatuses.get(id);
            return status === "QUEUED" || status === "RUNNING";
          }).length
        );
      } else {
        setHiddenPendingCount(0);
      }
    } catch (fetchError) {
      if (fetchId !== latestFetchIdRef.current) return;
      setError(getApiErrorMessage(fetchError));
    } finally {
      if (fetchId === latestFetchIdRef.current) {
        setLoading(false);
      }
    }
  }, [page, rowsPerPage, statusFilter]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const hasActiveJobs = useMemo(
    () =>
      hiddenPendingCount > 0 ||
      jobs.some((job) => job.status === "QUEUED" || job.status === "RUNNING"),
    [jobs, hiddenPendingCount]
  );

  // O diálogo guarda um snapshot do job; sem re-sincronizar, ele continuaria
  // exibindo RUNNING depois que o polling já trouxe DONE.
  useEffect(() => {
    setDetailJob((current) => {
      if (!current) return current;
      return jobs.find((job) => job.id === current.id) ?? current;
    });
  }, [jobs]);
  usePropitiousAreaJobsPolling(hasActiveJobs, fetchJobs);

  const handleStatusFilterChange = (
    _event: React.MouseEvent<HTMLElement>,
    nextStatus: PropitiousAreaJobStatusFilter | null
  ) => {
    if (!nextStatus) return;
    setStatusFilter(nextStatus);
    setPage(0);
  };

  const handlePageChange = (_event: unknown, nextPage: number) => {
    setPage(nextPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <HomeContainer>
      <StyledMain>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4">Áreas propícias solicitadas</Typography>
          <Typography variant="body4" className="text-[#676A70] pt-2">
            Cálculos de área propícia para o estado inteiro, processados em
            segundo plano. Você recebe um e-mail quando cada um terminar.
          </Typography>
        </Box>

        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <ToggleButtonGroup
            exclusive
            onChange={handleStatusFilterChange}
            size="small"
            value={statusFilter}
          >
            {STATUS_FILTERS.map((filter) => (
              <ToggleButton key={filter.value} value={filter.value}>
                {filter.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Tipo de energia</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Solicitado em</TableCell>
                <TableCell>Área propícia</TableCell>
                <TableCell>Potencial instalável</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && jobs.length === 0 ? (
                <TableRow>
                  <TableCell align="center" colSpan={6} sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : jobs.length === 0 ? (
                <TableRow>
                  <TableCell align="center" colSpan={6} sx={{ py: 4 }}>
                    <Typography variant="body4">
                      Nenhuma área propícia solicitada para o filtro atual.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                jobs.map((job) => (
                  <TableRow hover key={job.id}>
                    <TableCell>
                      {ENERGY_LABELS[job.energy_type] ?? job.energy_type}
                    </TableCell>
                    <TableCell>
                      <StatusChip status={job.status} />
                    </TableCell>
                    <TableCell>{formatIsoDate(job.created_at)}</TableCell>
                    <TableCell>{fmtArea(job.propitious_area_km2)}</TableCell>
                    <TableCell>{fmtPower(job.installable_gw)}</TableCell>
                    <TableCell align="right">
                      <Button
                        onClick={() => setDetailJob(job)}
                        size="sm"
                        type="button"
                        variant="outline"
                      >
                        <Eye />
                        Detalhes
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
          }
          labelRowsPerPage="Linhas por página:"
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />

        <Dialog
          fullWidth
          maxWidth="sm"
          onClose={() => setDetailJob(null)}
          open={Boolean(detailJob)}
        >
          <DialogTitle>Detalhes do cálculo estadual</DialogTitle>
          <DialogContent>
            {detailJob && (
              <Box sx={{ display: "grid", gap: 2 }}>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  }}
                >
                  <Box>
                    <Typography variant="miniText" className="text-[#676A70]">
                      Tipo de energia
                    </Typography>
                    <Typography variant="body4">
                      {ENERGY_LABELS[detailJob.energy_type] ??
                        detailJob.energy_type}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="miniText" className="text-[#676A70]">
                      Status
                    </Typography>
                    <StatusChip status={detailJob.status} />
                  </Box>
                  <Box>
                    <Typography variant="miniText" className="text-[#676A70]">
                      Solicitado em
                    </Typography>
                    <Typography variant="body4">
                      {formatIsoDate(detailJob.created_at)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="miniText" className="text-[#676A70]">
                      Concluído em
                    </Typography>
                    <Typography variant="body4">
                      {formatIsoDate(detailJob.finished_at)}
                    </Typography>
                  </Box>
                </Box>

                {detailJob.status === "DONE" && (
                  <>
                    <Divider />
                    <Box
                      sx={{
                        display: "grid",
                        gap: 2,
                        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                      }}
                    >
                      <Box>
                        <Typography
                          variant="miniText"
                          className="text-[#676A70]"
                        >
                          Área total
                        </Typography>
                        <Typography variant="body4">
                          {fmtArea(detailJob.total_area_km2)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          variant="miniText"
                          className="text-[#676A70]"
                        >
                          Área propícia
                        </Typography>
                        <Typography variant="body4">
                          {fmtArea(detailJob.propitious_area_km2)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          variant="miniText"
                          className="text-[#676A70]"
                        >
                          Potencial instalável
                        </Typography>
                        <Typography variant="body4">
                          {fmtPower(detailJob.installable_gw)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          variant="miniText"
                          className="text-[#676A70]"
                        >
                          Energia anual estimada
                        </Typography>
                        <Typography variant="body4">
                          {fmtEnergy(detailJob.annual_energy_gwh)}
                        </Typography>
                      </Box>
                    </Box>
                  </>
                )}

                {detailJob.status === "FAILED" && detailJob.error_message && (
                  <Alert severity="error">
                    <Typography variant="body4">
                      {detailJob.error_message}
                    </Typography>
                  </Alert>
                )}

                {detailJob.email_error && (
                  <Alert severity="warning">
                    <Typography variant="miniText">
                      Falha ao enviar o e-mail de notificação
                    </Typography>
                    <Typography variant="body4">
                      {detailJob.email_error}
                    </Typography>
                  </Alert>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDetailJob(null)}
              type="button"
              variant="outline"
            >
              Fechar
            </Button>
          </DialogActions>
        </Dialog>
      </StyledMain>
      <Footer />
    </HomeContainer>
  );
}
